// Level mapping utilities for Compensation Partner metrics

/**
 * Maps communication frequency text to numeric level (0-3)
 * @param {string[]} kommunikation - Communication array
 * @returns {number} Level 0-3
 */
export function getCommunicationFrequencyLevel(kommunikation) {
  if (!kommunikation || !Array.isArray(kommunikation)) return 0;
  
  // Find frequency-related entry (first array element typically contains frequency)
  const frequencyEntry = kommunikation.find(k => 
    k.includes("Sehr wenig") || k.includes("Wenig") || k.includes("Viel") || k.includes("prägender Stelleninhalt")
  );
  
  if (!frequencyEntry) return 0;
  
  // Map to levels based on documented patterns
  if (frequencyEntry.includes("prägender Stelleninhalt") || frequencyEntry.includes("75%")) {
    return 3; // Communication as main job content (>75%)
  } else if (frequencyEntry.includes("Viel") && frequencyEntry.includes("40%")) {
    return 2; // Much (>40%)
  } else if (frequencyEntry.includes("Wenig") && frequencyEntry.includes("40%")) {
    return 1; // Little (<40%)
  } else if (frequencyEntry.includes("Sehr wenig") && frequencyEntry.includes("15%")) {
    return 0; // Very little (<15%)
  }
  
  return 0; // Default to not specified
}

/**
 * Maps communication style text to numeric level (0-2)
 * @param {string[]} kommunikation - Communication array
 * @returns {number} Level 0-2
 */
export function getCommunicationStyleLevel(kommunikation) {
  if (!kommunikation || !Array.isArray(kommunikation)) return 0;
  
  // Find style-related entry (typically third element in array)
  const styleEntry = kommunikation.find(k => 
    k.includes("Sachebene") || k.includes("motivierend") || k.includes("überzeugend") || k.includes("inspirierend")
  );
  
  if (!styleEntry) return 0;
  
  // Map to levels based on documented patterns
  if (styleEntry.includes("ausgeprägt gewinnend") || styleEntry.includes("inspirierend")) {
    return 2; // Highly persuasive, inspiring
  } else if (styleEntry.includes("motivierend") || styleEntry.includes("überzeugend")) {
    return 1; // Motivating/persuasive
  } else if (styleEntry.includes("Sachebene")) {
    return 0; // Factual
  }
  
  return 0; // Default to factual
}

/**
 * Maps strategic influence text to numeric level (0-5)
 * @param {string[]} komplexität - Complexity array
 * @returns {number} Level 0-5
 */
export function getStrategicInfluenceLevel(komplexität) {
  if (!komplexität || !Array.isArray(komplexität)) return 0;
  
  // Find strategic influence entry (typically first element in array)
  const strategyEntry = komplexität.find(k => 
    k.includes("operative") || k.includes("strategische") || k.includes("Routine") || k.includes("strategische Probleme")
  );
  
  if (!strategyEntry) return 0;
  
  // Map to levels based on documented patterns (0-5 scale)
  if (strategyEntry.includes("praktisch ausschließlich strategische Probleme")) {
    return 5; // Almost exclusively strategic
  } else if (strategyEntry.includes("meist strategische Probleme") || strategyEntry.includes("oft daran beteiligt")) {
    return 4; // Mostly strategic problems
  } else if (strategyEntry.includes("sowohl operative als auch strategische")) {
    return 3; // Mixed operational and strategic
  } else if (strategyEntry.includes("manchmal sind die Ergebnisse Basis strategischer")) {
    return 2; // Operational with strategic impact
  } else if (strategyEntry.includes("meist operative Tätigkeiten") || strategyEntry.includes("fast nur Routineaufgaben")) {
    return 1; // Mostly operational/routine
  } else if (strategyEntry.includes("nur operative Tätigkeiten") || strategyEntry.includes("nur Routineaufgaben")) {
    return 0; // Only operational/routine
  }
  
  return 1; // Default to mostly operational
}

/**
 * Maps result visibility timeframe to numeric level (0-5)
 * @param {string[]} komplexität - Complexity array
 * @returns {number} Level 0-5
 */
export function getResultVisibilityLevel(komplexität) {
  if (!komplexität || !Array.isArray(komplexität)) return 0;
  
  // Find timeframe entry (typically second element in array)
  const timeframeEntry = komplexität.find(k => 
    k.includes("sofort") || k.includes("Tagen") || k.includes("Wochen") || 
    k.includes("Monaten") || k.includes("Jahr") || k.includes("Jahren")
  );
  
  if (!timeframeEntry) return 0;
  
  // Map to levels based on documented patterns (0-5 scale)
  if (timeframeEntry.includes("nach Jahren")) {
    return 5; // Years
  } else if (timeframeEntry.includes("in einem Jahr")) {
    return 4; // In a year
  } else if (timeframeEntry.includes("nach Monaten")) {
    return 3; // Months
  } else if (timeframeEntry.includes("nach Wochen")) {
    return 2; // Weeks
  } else if (timeframeEntry.includes("nach Tagen")) {
    return 1; // Days
  } else if (timeframeEntry.includes("sofort")) {
    return 0; // Immediately
  }
  
  return 1; // Default to days
}

/**
 * Maps quality control difficulty to numeric level (0-5)
 * @param {string[]} komplexität - Complexity array
 * @returns {number} Level 0-5
 */
export function getQualityControlLevel(komplexität) {
  if (!komplexität || !Array.isArray(komplexität)) return 0;
  
  // Find quality control entry (typically third element in array)
  const controlEntry = komplexität.find(k => 
    k.includes("kontrolliert") || k.includes("beurteilt") || k.includes("Kenngrößen")
  );
  
  if (!controlEntry) return 0;
  
  // Map to levels based on documented patterns (0-5 scale)
  if (controlEntry.includes("sehr leicht")) {
    return 0; // Very easy to control
  } else if (controlEntry.includes("wenig Aufwand")) {
    return 1; // Little effort to control
  } else if (controlEntry.includes("großem Aufwand")) {
    return 2; // High effort to control
  } else if (controlEntry.includes("Kenngrößen")) {
    return 3; // KPI-based assessment
  } else if (controlEntry.includes("Erfahrungen")) {
    return 4; // Experience-based assessment
  } else if (controlEntry.includes("späteren Ergebnisse")) {
    return 5; // Results-based assessment
  }
  
  return 1; // Default to little effort
}

/**
 * Maps quality control difficulty to descriptive text
 * @param {string[]} komplexität - Complexity array
 * @returns {string} Descriptive text for quality control
 */
export function getQualityControlDescription(komplexität) {
  if (!komplexität || !Array.isArray(komplexität)) return "Nicht spezifiziert";
  
  const controlEntry = komplexität.find(k => 
    k.includes("kontrolliert") || k.includes("beurteilt") || k.includes("Kenngrößen")
  );
  
  if (!controlEntry) return "Nicht spezifiziert";
  
  if (controlEntry.includes("Kenngrößen")) {
    return "KPI-basiert";
  } else if (controlEntry.includes("großem Aufwand")) {
    return "Hoher Aufwand";
  } else if (controlEntry.includes("wenig Aufwand")) {
    return "Geringer Aufwand";
  } else if (controlEntry.includes("sehr leicht")) {
    return "Sehr einfach";
  } else if (controlEntry.includes("Erfahrungen")) {
    return "Erfahrungsbasiert";
  } else if (controlEntry.includes("späteren Ergebnisse")) {
    return "Ergebnisbasiert";
  }
  
  return "Standard";
}

/**
 * Label arrays for display - adjusted to match actual level ranges
 */
export const LEVEL_LABELS = {
  communication: {
    frequency: ['Sehr wenig (<15%)', 'Wenig (<40%)', 'Viel (>40%)', 'Prägend (>75%)'],
    style: ['Sachebene', 'Motivierend', 'Inspirierend']
  },
  complexity: {
    strategic: ['Nur Routine', 'Meist Routine', 'Routine + strategisch', 'Gemischt', 'Meist strategisch', 'Nur strategisch'],
    visibility: ['Sofort', 'Tage', 'Wochen', 'Monate', '1 Jahr', 'Jahre'],
    qualityControl: ['Sehr einfach', 'Geringer Aufwand', 'Hoher Aufwand', 'KPI-basiert', 'Erfahrungsbasiert', 'Ergebnisbasiert']
  }
};